"use client";

/**
 * Step 1 — Sequence. A radio list of the job's non-archived sequences that
 * have at least one step (archived + step-less ones are filtered out
 * upstream). Sequences with steps still missing required content render
 * disabled. Continue is gated on a pick.
 */

import { useRouter } from "next/navigation";

import { SubmitButton } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StepFooter, StepScrollArea } from "@/components/wizard";
import { cn } from "@/lib/utils";
import type { SequenceEnrollmentBlockReason } from "@/features/sequences/hooks/use-sequences";

import { ENROLLMENT_ACCENT_TEXT, ENROLLMENT_PRIMARY_BTN } from "../../lib/theme";
import { useConfigureWizard } from "./context";

/** Per-reason copy for the disabled sequence rows in the picker. Keeps the
 * inline badge + hover tooltip in sync with the server-computed reason. */
const SEQUENCE_BLOCK_META: Record<
  SequenceEnrollmentBlockReason,
  { label: string; tooltip: string; icon: IconName; tone: string }
> = {
  unconfigured_steps: {
    label: "Needs configuration",
    tooltip:
      "This sequence has steps that need configuration before you can enroll.",
    icon: "IconExclamationCircle",
    tone: "text-amber-500",
  },
  mailbox_missing: {
    label: "No sender mailbox",
    tooltip:
      "This sequence has no sending mailbox. Select a sender and mailbox in the sequence settings before you can enroll.",
    icon: "IconExclamationCircle",
    tone: "text-amber-500",
  },
  mailbox_needs_reconnect: {
    label: "Sender needs reconnect",
    tooltip:
      "The sender's mailbox authentication expired. Reconnect it in Settings → Connected Accounts before enrolling.",
    icon: "IconExclamationCircle",
    tone: "text-amber-500",
  },
  mailbox_disconnected: {
    label: "Sender mailbox disconnected",
    tooltip:
      "The sender's mailbox is disconnected. Reconnect it in Settings → Connected Accounts before enrolling.",
    icon: "IconCircleX",
    tone: "text-destructive",
  },
};

export function StepSequence() {
  const {
    jobId,
    eligibleSequences,
    hasAnySequences,
    sequencesLoading,
    hasMoreSequences,
    loadingMoreSequences,
    loadMoreSequences,
    sequenceId,
    selectSequence,
    setStep,
    onCancel,
  } = useConfigureWizard();
  const router = useRouter();

  const goToSequences = () => {
    if (!jobId) return;
    onCancel();
    router.push(`/jobs/${jobId}/sequences`);
  };

  return (
    <>
      <StepScrollArea contentClassName="gap-4">
        {!sequencesLoading && eligibleSequences.length === 0 ? null : (
          <p className="text-sm text-muted-foreground">
            Choose a sequence for these candidates.
          </p>
        )}

        {sequencesLoading ? (
          <div className="flex flex-col gap-2" aria-label="Loading sequences">
            <div className="h-12 animate-pulse rounded-lg bg-foreground/4" />
            <div className="h-12 animate-pulse rounded-lg bg-foreground/4" />
          </div>
        ) : eligibleSequences.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-border bg-contrast/[0.02] px-6 py-10 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-[#87FFA5]/10">
              <Icon
                name="IconPaperPlane"
                size={22}
                className={ENROLLMENT_ACCENT_TEXT}
              />
            </div>
            <div className="flex max-w-xs flex-col gap-1.5">
              <h3 className="text-sm font-semibold text-foreground">
                {hasAnySequences
                  ? "No sequences ready to enroll"
                  : "No sequences yet"}
              </h3>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {hasAnySequences
                  ? "This job's sequences aren't ready yet — each needs at least one step before you can enroll these candidates."
                  : "Create a sequence for this job, then come back to enroll these candidates."}
              </p>
            </div>
            <SubmitButton
              type="button"
              size="sm"
              onClick={goToSequences}
              disabled={!jobId}
              className={ENROLLMENT_PRIMARY_BTN}
              icon={
                <Icon
                  name={hasAnySequences ? "IconArrowUpRight" : "IconPlusMedium"}
                  size={15}
                />
              }
            >
              {hasAnySequences ? "Manage sequences" : "Create a sequence"}
            </SubmitButton>
          </div>
        ) : (
          <fieldset
            className="flex flex-col gap-2"
            aria-label="Sequence picker"
          >
            {eligibleSequences.map((seq) => {
              const selected = sequenceId === seq.id;
              // A non-enrollable sequence (unconfigured steps OR the sender
              // mailbox is disconnected / needs reconnect) can't accept new
              // enrollments — the backend blocks the preview-create with a
              // matching code, so it's not selectable here: disabled + an
              // explanatory tooltip. `enrollment_blocked_reason` is the
              // single server-computed reason; fall back to the legacy
              // unconfigured count for older payloads.
              const blockReason =
                seq.enrollment_blocked_reason ??
                ((seq.unconfigured_step_count ?? 0) > 0
                  ? "unconfigured_steps"
                  : null);
              const needsConfig = blockReason !== null;
              const blockMeta = blockReason
                ? SEQUENCE_BLOCK_META[blockReason]
                : null;

              const row = (
                <label
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg border p-3 transition-colors",
                    needsConfig
                      ? "cursor-not-allowed border-border bg-contrast/[0.02] opacity-60"
                      : selected
                        ? "cursor-pointer border-[#5FBF77] bg-[#87FFA5]/5"
                        : "cursor-pointer border-border bg-contrast/[0.02] hover:border-foreground/15 hover:bg-contrast/4",
                  )}
                >
                  <input
                    type="radio"
                    name="enroll-configure-sequence"
                    value={seq.id}
                    checked={selected}
                    disabled={needsConfig}
                    onChange={() => selectSequence(seq.id)}
                    className="sr-only"
                  />
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#87FFA5]/10">
                    <Icon
                      name="IconPaperPlane"
                      size={16}
                      className={ENROLLMENT_ACCENT_TEXT}
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                    <span className="truncate text-sm font-medium text-foreground">
                      {seq.name}
                    </span>
                    {needsConfig && blockMeta ? (
                      <span
                        className={cn(
                          "flex items-center gap-1 text-xs",
                          blockMeta.tone,
                        )}
                      >
                        <Icon name={blockMeta.icon} size={12} />
                        {blockMeta.label}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">
                        {seq.step_count}{" "}
                        {seq.step_count === 1 ? "step" : "steps"}
                      </span>
                    )}
                  </div>
                  <div
                    className={cn(
                      "flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors",
                      selected ? "border-[#5FBF77] bg-[#87FFA5]" : "border-contrast/25",
                    )}
                  >
                    {selected && <div className="size-1.5 rounded-full bg-black" />}
                  </div>
                </label>
              );

              if (needsConfig && blockMeta) {
                return (
                  <Tooltip key={seq.id}>
                    <TooltipTrigger asChild>
                      {/* span wrapper so the tooltip fires over the
                          non-interactive (disabled) row */}
                      <span className="inline-flex w-full">{row}</span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {blockMeta.tooltip}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return <div key={seq.id}>{row}</div>;
            })}
            {hasMoreSequences && (
              <button
                type="button"
                onClick={loadMoreSequences}
                disabled={loadingMoreSequences}
                className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground disabled:cursor-default disabled:opacity-60"
              >
                {loadingMoreSequences ? (
                  <Icon
                    name="IconLoadingCircle"
                    size={14}
                    className="animate-spin"
                  />
                ) : (
                  <Icon name="IconArrowDown" size={14} />
                )}
                {loadingMoreSequences ? "Loading…" : "Load more sequences"}
              </button>
            )}
          </fieldset>
        )}
      </StepScrollArea>
      <StepFooter>
        <span />
        <SubmitButton
          type="button"
          size="sm"
          onClick={() => setStep(2)}
          disabled={!sequenceId}
          className={ENROLLMENT_PRIMARY_BTN}
        >
          Continue
        </SubmitButton>
      </StepFooter>
    </>
  );
}
